import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from "../axiosInstance";
import Swal from "sweetalert2";
import Footer from '../components/Footer';
import DocViewModal from '../components/DocViewModal';

const fmt = (n) => '₹ ' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const fmtDate = (s) => { if (!s) return '—'; const d = new Date(s + 'T00:00:00'); return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }); };

function Leaddetails() {
    const { id } = useParams(); // Get the lead ID from URL
    const navigate = useNavigate();
    const [lead, setLead] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [converting, setConverting] = useState(false);
    
    // Quotations for this lead
    const [quotations,  setQuotations]  = useState([]);
    const [viewingDoc,  setViewingDoc]  = useState(null);

    // Lead Interactions States
    const [interactions, setInteractions] = useState([]);
    const [interactionsLoading, setInteractionsLoading] = useState(false);
    const [showAddInteraction, setShowAddInteraction] = useState(false);
    const [newInteraction, setNewInteraction] = useState({
        interaction_type: '',
        interaction_date: new Date().toISOString().split('T')[0],
        interaction_time: new Date().toTimeString().split(' ')[0].substring(0, 5),
        description: '',
        outcome: '',
        follow_up_date: '',
        notes: ''
    });
    const [submittingInteraction, setSubmittingInteraction] = useState(false);

    // Fetch specific lead data
    const fetchLeadDetails = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await axiosInstance.get(`/leads/details/${id}`);
            console.log("Lead details response:", response.data);
            setLead(response.data.data || response.data);
        } catch (error) {
            console.error("Error fetching lead details:", error);
            setError("Failed to fetch lead details");
            Swal.fire({
                icon: 'error',
                title: 'Error!',
                text: 'Failed to fetch lead details',
                confirmButtonText: 'Go Back'
            }).then(() => {
                navigate('/lead'); // Navigate back to leads list
            });
        } finally {
            setLoading(false);
        }
    };

    // Fetch quotations for this lead
    const fetchLeadQuotations = async () => {
        try {
            const res = await axiosInstance.get(`/quotations?lead_id=${id}`);
            setQuotations(res.data.data || []);
        } catch {
            setQuotations([]);
        }
    };

    // Fetch lead interactions
    const fetchLeadInteractions = async () => {
        try {
            setInteractionsLoading(true);
            const response = await axiosInstance.get(`/leadInteractions/${id}`);
            // Filter interactions for this specific lead
            const leadInteractions = response.data.data?.filter(interaction => 
                interaction.lead_id === id
            ) || [];
            
            // Sort by date (most recent first)
            leadInteractions.sort((a, b) => new Date(b.interaction_date) - new Date(a.interaction_date));
            setInteractions(leadInteractions);
        } catch (error) {
            console.error("Error fetching lead interactions:", error);
            // Don't show error for interactions as it's not critical
        } finally {
            setInteractionsLoading(false);
        }
    };

    // Add new interaction
    const handleAddInteraction = async (e) => {
        e.preventDefault();
        
        if (!newInteraction.interaction_type || !newInteraction.description) {
            Swal.fire({
                icon: 'warning',
                title: 'Required Fields',
                text: 'Please fill in interaction type and description'
            });
            return;
        }

        try {
            setSubmittingInteraction(true);
            
            const interactionData = {
                ...newInteraction,
                lead_id: id,
                interaction_datetime: `${newInteraction.interaction_date} ${newInteraction.interaction_time}:00`
            };

            const response = await axiosInstance.post('/leadInteractions', interactionData);
            
            if (response.data.success) {
                Swal.fire({
                    icon: 'success',
                    title: 'Success!',
                    text: 'Lead interaction added successfully',
                    timer: 2000,
                    showConfirmButton: false
                });

                // Reset form
                setNewInteraction({
                    interaction_type: '',
                    interaction_date: new Date().toISOString().split('T')[0],
                    interaction_time: new Date().toTimeString().split(' ')[0].substring(0, 5),
                    description: '',
                    outcome: '',
                    follow_up_date: '',
                    notes: ''
                });
                
                setShowAddInteraction(false);
                await fetchLeadInteractions(); // Refresh interactions
            }
        } catch (error) {
            console.error("Error adding interaction:", error);
            Swal.fire({
                icon: 'error',
                title: 'Error!',
                text: error.response?.data?.message || 'Failed to add interaction'
            });
        } finally {
            setSubmittingInteraction(false);
        }
    };

    // Convert lead to account and contact
    const handleConvertLead = async () => {
        try {
            // Show confirmation dialog
            const result = await Swal.fire({
                title: 'Convert Lead?',
                text: `Are you sure you want to convert "${lead.company_name}" to Account and Contact? This action cannot be undone.`,
                icon: 'question',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Yes, Convert!',
                cancelButtonText: 'Cancel'
            });

            if (result.isConfirmed) {
                setConverting(true);
                
                const response = await axiosInstance.post(`/leads/convert/${id}`);
                console.log("Response from convert:", response.data)
                if (response.data.success) {
                    // Show success message with conversion details
                    await Swal.fire({
                        icon: 'success',
                        title: 'Lead Converted Successfully!',
                        html: `
                            <div class="text-start">
                                <p><strong>Lead:</strong> ${lead.company_name}</p>
                                <p><strong>Account Created:</strong> ${response.data.data.account.company_name}</p>
                                <p><strong>Contact Created:</strong> ${response.data.data.contact.first_name}</p>
                                <p><strong>Lead Stage:</strong> ${response.data.data.lead.status}</p>
                                <p><strong>Converted At:</strong> ${new Date(response.data.data.conversion_details.converted_at).toLocaleString()}</p>
                            </div>
                        `,
                        confirmButtonText: 'OK'
                    });
                    
                    // Refresh lead details to show updated status
                    await fetchLeadDetails();
                } else {
                    throw new Error(response.data.message || 'Conversion failed');
                }
            }
        } catch (error) {
            console.error("Error converting lead:", error);
            
            let errorMessage = 'Failed to convert lead';
            if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.message) {
                errorMessage = error.message;
            }
            
            Swal.fire({
                icon: 'error',
                title: 'Conversion Failed!',
                text: errorMessage,
                confirmButtonText: 'OK'
            });
        } finally {
            setConverting(false);
        }
    };

    // Check if lead is already converted using isconverted field
    const isLeadConverted = () => {
        return lead?.isconverted || lead?.account_id || lead?.contact_id;
    };

    // Get interaction type badge color
    const getInteractionTypeBadge = (type) => {
        const badges = {
            'Call': 'bg-primary',
            'Email': 'bg-info',
            'Meeting': 'bg-success',
            'Follow-up': 'bg-warning',
            'Presentation': 'bg-secondary',
            'Proposal': 'bg-dark',
            'Other': 'bg-light text-dark'
        };
        return badges[type] || 'bg-light text-dark';
    };

    useEffect(() => {
        if (id) {
            fetchLeadDetails();
            fetchLeadInteractions();
            fetchLeadQuotations();
        }
    }, [id]);

    if (loading) {
        return (
            <div className="page-wrapper">
                <div className="content container-fluid">
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error || !lead) {
        return (
            <div className="page-wrapper">
                <div className="content container-fluid">
                    <div className="alert alert-danger" role="alert">
                        {error || "Lead not found"}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div>
            {/* Page Wrapper */}
            <div className="page-wrapper">
                <div className="content container-fluid">
                
                    {/* Page Header */}
                    <div className="page-header">
                        <div className="content-page-header">
                            <h5>Lead Details</h5>
                            <div className="list-btn">
                                <ul className="filter-list">
                                    <li>
                                        <button 
                                            className="btn btn-secondary me-2"
                                            onClick={() => navigate('/lead')}
                                        >
                                            <i className="fa fa-arrow-left me-2"></i>Back to Leads
                                        </button>
                                    </li>
                                    <li>
                                        <button 
                                            className="btn btn-primary me-2"
                                            onClick={() => navigate('/add-lead', { state: { lead, isEdit: true } })}
                                        >
                                            <i className="fa fa-edit me-2"></i>Edit Lead
                                        </button>
                                    </li>
                                    <li>
                                        <button 
                                            className={`btn ${isLeadConverted() ? 'btn-success' : 'btn-warning'}`}
                                            onClick={handleConvertLead}
                                            disabled={converting || isLeadConverted()}
                                        >
                                            {converting ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                    Converting...
                                                </>
                                            ) : isLeadConverted() ? (
                                                <>
                                                    <i className="fa fa-check me-2"></i>Converted
                                                </>
                                            ) : (
                                                <>
                                                    <i className="fa fa-exchange-alt me-2"></i>Convert Lead
                                                </>
                                            )}
                                        </button>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                    {/* /Page Header */}

                    {/* Show conversion success banner if converted */}
                    {isLeadConverted() && (
                        <div className="alert alert-success alert-dismissible fade show" role="alert">
                            <div className="d-flex align-items-center">
                                <i className="fa fa-check-circle me-2"></i>
                                <div>
                                    <strong>Lead Converted!</strong> This lead has been successfully converted to Account and Contact.
                                    {lead.converted_at && (
                                        <div className="small text-muted">
                                            Converted on: {new Date(lead.converted_at).toLocaleString()}
                                        </div>
                                    )}
                                    {/* Show current lead stage */}
                                    <div className="small">
                                        Lead Stage: <span className={`badge ${
                                            lead.status === 'New' ? 'bg-primary' :
                                            lead.status === 'Pitching' ? 'bg-info' :
                                            lead.status === 'Profile Shared' ? 'bg-secondary' :
                                            lead.status === 'Secure Lead' ? 'bg-success' :
                                            lead.status === 'Proposal Sent' ? 'bg-warning' :
                                            lead.status === 'Won' ? 'bg-success' :
                                            lead.status === 'Lost' ? 'bg-danger' :
                                            'bg-light text-dark'
                                        }`}>
                                            {lead.status || 'N/A'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="card customer-details-group">
                        <div className="card-body">
                            <div className="row align-items-center">					
                                <div className="col-xl-3 col-lg-4 col-md-6 col-12">
                                    <div className="customer-details">
                                        <div className="d-flex align-items-center">
                                            <span className="customer-widget-icon d-inline-flex">
                                                <i className="fe fe-briefcase"></i>
                                            </span>
                                            <div className="customer-details-cont">
                                                <h6>Lead Name</h6>
                                                <p>{lead.company_name || 'N/A'}</p>
                                            </div>
                                        </div>
                                    </div> 
                                </div>
                                <div className="col-xl-3 col-lg-4 col-md-6 col-12">
                                    <div className="customer-details">
                                        <div className="d-flex align-items-center">
                                            <span className="customer-widget-icon d-inline-flex">
                                                <i className="fe fe-mail"></i>
                                            </span>
                                            <div className="customer-details-cont">
                                                <h6>Email Address</h6>
                                                <p>
                                                    {lead.email ? (
                                                        <a href={`mailto:${lead.email}`} className="text-primary">
                                                            {lead.email}
                                                        </a>
                                                    ) : (
                                                        'N/A'
                                                    )}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xl-3 col-lg-4 col-md-6 col-12">
                                    <div className="customer-details">
                                        <div className="d-flex align-items-center">
                                            <span className="customer-widget-icon d-inline-flex">
                                                <i className="fe fe-phone"></i>
                                            </span>
                                            <div className="customer-details-cont">
                                                <h6>Phone Number</h6>
                                                <p>
                                                    {lead.phone ? (
                                                        <a href={`tel:${lead.phone}`} className="text-primary">
                                                            {lead.phone}
                                                        </a>
                                                    ) : (
                                                        'N/A'
                                                    )}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xl-3 col-lg-4 col-md-6 col-12">
                                    <div className="customer-details">
                                        <div className="d-flex align-items-center">
                                            <span className="customer-widget-icon d-inline-flex">
                                                <i className="fab fa-whatsapp"></i>
                                            </span>
                                            <div className="customer-details-cont">
                                                <h6>WhatsApp Number</h6>
                                                <p>
                                                    {lead.whatsapp_no ? (
                                                        <a 
                                                            href={`https://wa.me/${lead.whatsapp_no.replace(/\D/g, '')}`} 
                                                            target="_blank" 
                                                            rel="noopener noreferrer"
                                                            className="text-primary"
                                                        >
                                                            {lead.whatsapp_no}
                                                        </a>
                                                    ) : (
                                                        'N/A'
                                                    )}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="col-xl-3 col-lg-4 col-md-6 col-12">
                                    <div className="customer-details">
                                        <div className="d-flex align-items-center">
                                            <span className="customer-widget-icon d-inline-flex">
                                                <i className="fe fe-layers"></i>
                                            </span>
                                            <div className="customer-details-cont">
                                                <h6>Industry</h6>
                                                <p>{lead.industry || 'N/A'}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xl-3 col-lg-4 col-md-6 col-12">
                                    <div className="customer-details">
                                        <div className="d-flex align-items-center">
                                            <span className="customer-widget-icon d-inline-flex">
                                                <i className="fe fe-layers"></i>
                                            </span>
                                            <div className="customer-details-cont">
                                                <h6>Sub Industry</h6>
                                                <p>{lead.sub_industry || 'N/A'}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xl-3 col-lg-4 col-md-6 col-12">
                                    <div className="customer-details">
                                        <div className="d-flex align-items-center">
                                            <span className="customer-widget-icon d-inline-flex">
                                                <i className="fe fe-map-pin"></i>
                                            </span>
                                            <div className="customer-details-cont">
                                                <h6>Company Address</h6>
                                                <p>{lead.address_line1 || ''}</p>
                                                <p>{lead.address_line2 || ''}</p>
                                                <p>{lead.city?.name || ''}</p>
                                                <p>{lead.state?.name || ''}</p>
                                                <p>{lead.country?.name || ''}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xl-3 col-lg-4 col-md-6 col-12">
                                    <div className="customer-details">
                                        <div className="d-flex align-items-center">
                                            <span className="customer-widget-icon d-inline-flex">
                                                <i className="fe fe-user"></i>
                                            </span>
                                            <div className="customer-details-cont">
                                                <h6>Lead Source</h6>
                                                <p>{lead.source || 'N/A'}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xl-3 col-lg-4 col-md-6 col-12">
                                    <div className="customer-details">
                                        <div className="d-flex align-items-center">
                                            <span className="customer-widget-icon d-inline-flex">
                                                <i className="fe fe-flag"></i>
                                            </span>
                                            <div className="customer-details-cont">
                                                <h6>Lead Stage</h6>
                                                <p>
                                                    <span className={`badge ${
                                                        lead.status === 'New' ? 'bg-primary' :
                                                        lead.status === 'Pitching' ? 'bg-info' :
                                                        lead.status === 'Profile Shared' ? 'bg-secondary' :
                                                        lead.status === 'Secure Lead' ? 'bg-success' :
                                                        lead.status === 'Proposal Sent' ? 'bg-warning' :
                                                        lead.status === 'Won' ? 'bg-success' :
                                                        lead.status === 'Lost' ? 'bg-danger' :
                                                        'bg-light text-dark'
                                                    }`}>
                                                        {lead.status || 'N/A'}
                                                    </span>
                                                    {/* Show conversion status indicator */}
                                                    {lead.isconverted && (
                                                        <span className="badge bg-success ms-2">
                                                            <i className="fa fa-check me-1"></i>Converted
                                                        </span>
                                                    )}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xl-3 col-lg-4 col-md-6 col-12">
                                    <div className="customer-details">
                                        <div className="d-flex align-items-center">
                                            <span className="customer-widget-icon d-inline-flex">
                                                <i className="fe fe-calendar"></i>
                                            </span>
                                            <div className="customer-details-cont">
                                                <h6>Created Date</h6>
                                                <p>
                                                    {lead.created_at ? 
                                                        new Date(lead.created_at).toLocaleDateString('en-US', {
                                                            year: 'numeric',
                                                            month: 'long',
                                                            day: 'numeric'
                                                        }) : 'N/A'
                                                    }
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xl-3 col-lg-4 col-md-6 col-12">
                                    <div className="customer-details">
                                        <div className="d-flex align-items-center">
                                            <span className="customer-widget-icon d-inline-flex">
                                                <i className="fe fe-edit"></i>
                                            </span>
                                            <div className="customer-details-cont">
                                                <h6>Last Updated</h6>
                                                <p>
                                                    {lead.updated_at ? 
                                                        new Date(lead.updated_at).toLocaleDateString('en-US', {
                                                            year: 'numeric',
                                                            month: 'long',
                                                            day: 'numeric'
                                                        }) : 'N/A'
                                                    }
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Show converted at date if lead is converted */}
                                {lead.converted_at && (
                                    <div className="col-xl-3 col-lg-4 col-md-6 col-12">
                                        <div className="customer-details">
                                            <div className="d-flex align-items-center">
                                                <span className="customer-widget-icon d-inline-flex">
                                                    <i className="fe fe-check-circle text-success"></i>
                                                </span>
                                                <div className="customer-details-cont">
                                                    <h6>Converted Date</h6>
                                                    <p>
                                                        {new Date(lead.converted_at).toLocaleDateString('en-US', {
                                                            year: 'numeric',
                                                            month: 'long',
                                                            day: 'numeric'
                                                        })}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Show linked account and contact if converted */}
                                {lead.account_id && (
                                    <div className="col-xl-3 col-lg-4 col-md-6 col-12">
                                        <div className="customer-details">
                                            <div className="d-flex align-items-center">
                                                <span className="customer-widget-icon d-inline-flex">
                                                    <i className="fe fe-briefcase text-primary"></i>
                                                </span>
                                                <div className="customer-details-cont">
                                                    <h6>Linked Account</h6>
                                                    <p>
                                                        <button 
                                                            className="btn btn-sm btn-outline-primary"
                                                            onClick={() => navigate(`/account-details/${lead.account_id}`)}
                                                        >
                                                            <i className="fa fa-external-link-alt me-1"></i>
                                                            View Account
                                                        </button>
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {lead.contact_id && (
                                    <div className="col-xl-3 col-lg-4 col-md-6 col-12">
                                        <div className="customer-details">
                                            <div className="d-flex align-items-center">
                                                <span className="customer-widget-icon d-inline-flex">
                                                    <i className="fe fe-user text-info"></i>
                                                </span>
                                                <div className="customer-details-cont">
                                                    <h6>Linked Contact</h6>
                                                    <p>
                                                        <button 
                                                            className="btn btn-sm btn-outline-info"
                                                            onClick={() => navigate(`/contacts/${lead.contact_id}`)}
                                                        >
                                                            <i className="fa fa-external-link-alt me-1"></i>
                                                            View Contact
                                                        </button>
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Additional fields if available in your lead data */}
                                {lead.designation && (
                                    <div className="col-xl-3 col-lg-4 col-md-6 col-12">
                                        <div className="customer-details">
                                            <div className="d-flex align-items-center">
                                                <span className="customer-widget-icon d-inline-flex">
                                                    <i className="fe fe-award"></i>
                                                </span>
                                                <div className="customer-details-cont">
                                                    <h6>Designation</h6>
                                                    <p>{lead.designation}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                {lead.lead_source && (
                                    <div className="col-xl-3 col-lg-4 col-md-6 col-12">
                                        <div className="customer-details">
                                            <div className="d-flex align-items-center">
                                                <span className="customer-widget-icon d-inline-flex">
                                                    <i className="fe fe-share-2"></i>
                                                </span>
                                                <div className="customer-details-cont">
                                                    <h6>Lead Source</h6>
                                                    <p>{lead.lead_source}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                {lead.notes && (
                                    <div className="col-12">
                                        <div className="customer-details">
                                            <div className="d-flex align-items-start">
                                                <span className="customer-widget-icon d-inline-flex">
                                                    <i className="fe fe-file-text"></i>
                                                </span>
                                                <div className="customer-details-cont">
                                                    <h6>Notes</h6>
                                                    <p className="text-muted" style={{ whiteSpace: 'pre-wrap' }}>
                                                        {lead.notes}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Quotations Section */}
                    <div className="card mt-4">
                        <div className="card-header d-flex justify-content-between align-items-center">
                            <h5 className="card-title mb-0">
                                <i className="fas fa-file-invoice me-2"></i>Quotations
                                <span className="badge bg-primary ms-2">{quotations.length}</span>
                            </h5>
                            <button
                                className="btn btn-primary btn-sm"
                                onClick={() => navigate('/quotation')}
                            >
                                <i className="fas fa-plus me-1"></i>Create Quotation
                            </button>
                        </div>
                        <div className="card-body p-0">
                            {quotations.length === 0 ? (
                                <div className="text-center py-5 text-muted">
                                    <i className="fas fa-file-alt" style={{ fontSize: '2.5rem', opacity: .25 }}></i>
                                    <p className="mt-2 mb-0">No quotations created for this lead yet</p>
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-hover mb-0">
                                        <thead className="thead-light">
                                            <tr>
                                                <th>Document No.</th>
                                                <th>Date</th>
                                                <th>Customer</th>
                                                <th>Amount</th>
                                                <th>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {quotations.map(q => (
                                                <tr key={q.id}>
                                                    <td>
                                                        <span className="badge" style={{ background: '#e0e7ff', color: '#0a1f5c', fontSize: 13, fontWeight: 700 }}>
                                                            {q.number}
                                                        </span>
                                                    </td>
                                                    <td>{fmtDate(q.date_str)}</td>
                                                    <td>{q.customer_name || '—'}</td>
                                                    <td><strong>{fmt(q.subtotal)}</strong></td>
                                                    <td>
                                                        <button
                                                            className="btn btn-sm btn-outline-primary"
                                                            onClick={() => setViewingDoc(q)}
                                                        >
                                                            <i className="fas fa-eye me-1"></i>View
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Lead Interactions Section */}
                    <div className="card mt-4">
                        <div className="card-header d-flex justify-content-between align-items-center">
                            <h5 className="card-title mb-0">
                                <i className="fe fe-message-circle me-2"></i>Lead Interactions
                            </h5>
                            <button 
                                className="btn btn-primary btn-sm"
                                onClick={() => setShowAddInteraction(!showAddInteraction)}
                            >
                                <i className="fa fa-plus me-2"></i>Add Interaction
                            </button>
                        </div>

                        {/* Add Interaction Form */}
                        {showAddInteraction && (
                            <div className="card-body border-bottom bg-light">
                                <h6 className="mb-3">Add New Interaction</h6>
                                <form onSubmit={handleAddInteraction}>
                                    <div className="row">
                                        <div className="col-md-3">
                                            <div className="mb-3">
                                                <label className="form-label">Interaction Type*</label>
                                                <select 
                                                    className="form-select"
                                                    value={newInteraction.interaction_type}
                                                    onChange={(e) => setNewInteraction({
                                                        ...newInteraction,
                                                        interaction_type: e.target.value
                                                    })}
                                                    required
                                                >
                                                    <option value="">Select Type</option>
                                                    <option value="Call">Call</option>
                                                    <option value="Email">Email</option>
                                                    <option value="Meeting">Meeting</option>
                                                    <option value="Follow-up">Follow-up</option>
                                                    <option value="Presentation">Presentation</option>
                                                    <option value="Proposal">Proposal</option>
                                                    <option value="Other">Other</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div className="col-md-2">
                                            <div className="mb-3">
                                                <label className="form-label">Date*</label>
                                                <input 
                                                    type="date"
                                                    className="form-control"
                                                    value={newInteraction.interaction_date}
                                                    onChange={(e) => setNewInteraction({
                                                        ...newInteraction,
                                                        interaction_date: e.target.value
                                                    })}
                                                    required
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-2">
                                            <div className="mb-3">
                                                <label className="form-label">Time</label>
                                                <input 
                                                    type="time"
                                                    className="form-control"
                                                    value={newInteraction.interaction_time}
                                                    onChange={(e) => setNewInteraction({
                                                        ...newInteraction,
                                                        interaction_time: e.target.value
                                                    })}
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-5">
                                            <div className="mb-3">
                                                <label className="form-label">Description*</label>
                                                <input 
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="Brief description of interaction"
                                                    value={newInteraction.description}
                                                    onChange={(e) => setNewInteraction({
                                                        ...newInteraction,
                                                        description: e.target.value
                                                    })}
                                                    required
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-4">
                                            <div className="mb-3">
                                                <label className="form-label">Outcome</label>
                                                <input 
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="Result or outcome"
                                                    value={newInteraction.outcome}
                                                    onChange={(e) => setNewInteraction({
                                                        ...newInteraction,
                                                        outcome: e.target.value
                                                    })}
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-3">
                                            <div className="mb-3">
                                                <label className="form-label">Follow-up Date</label>
                                                <input 
                                                    type="date"
                                                    className="form-control"
                                                    value={newInteraction.follow_up_date}
                                                    onChange={(e) => setNewInteraction({
                                                        ...newInteraction,
                                                        follow_up_date: e.target.value
                                                    })}
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-5">
                                            <div className="mb-3">
                                                <label className="form-label">Notes</label>
                                                <textarea 
                                                    className="form-control"
                                                    rows="2"
                                                    placeholder="Additional notes"
                                                    value={newInteraction.notes}
                                                    onChange={(e) => setNewInteraction({
                                                        ...newInteraction,
                                                        notes: e.target.value
                                                    })}
                                                ></textarea>
                                            </div>
                                        </div>
                                        <div className="col-12">
                                            <div className="d-flex gap-2">
                                                <button 
                                                    type="submit" 
                                                    className="btn btn-primary"
                                                    disabled={submittingInteraction}
                                                >
                                                    {submittingInteraction ? (
                                                        <>
                                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                            Adding...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <i className="fa fa-save me-2"></i>Add Interaction
                                                        </>
                                                    )}
                                                </button>
                                                <button 
                                                    type="button" 
                                                    className="btn btn-secondary"
                                                    onClick={() => {
                                                        setShowAddInteraction(false);
                                                        setNewInteraction({
                                                            interaction_type: '',
                                                            interaction_date: new Date().toISOString().split('T')[0],
                                                            interaction_time: new Date().toTimeString().split(' ')[0].substring(0, 5),
                                                            description: '',
                                                            outcome: '',
                                                            follow_up_date: '',
                                                            notes: ''
                                                        });
                                                    }}
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        )}

                        {/* Interactions History */}
                        <div className="card-body">
                            {interactionsLoading ? (
                                <div className="text-center py-4">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading interactions...</span>
                                    </div>
                                </div>
                            ) : interactions.length === 0 ? (
                                <div className="text-center py-5 text-muted">
                                    <i className="fe fe-message-circle" style={{fontSize: '3rem', opacity: 0.3}}></i>
                                    <h5 className="mt-3">No Interactions Yet</h5>
                                    <p>Start tracking your communications with this lead by adding your first interaction.</p>
                                </div>
                            ) : (
                                <div className="interaction-timeline">
                                    <h6 className="mb-4">Interaction History ({interactions.length})</h6>
                                    {interactions.map((interaction, index) => (
                                        <div key={interaction.id} className="interaction-item mb-4 pb-4 border-bottom">
                                            <div className="row">
                                                <div className="col-md-8">
                                                    <div className="d-flex align-items-start mb-2">
                                                        <span className={`badge ${getInteractionTypeBadge(interaction.interaction_type)} me-3`}>
                                                            {interaction.interaction_type}
                                                        </span>
                                                        <div className="flex-grow-1">
                                                            <h6 className="mb-1">{interaction.description}</h6>
                                                            <div className="text-muted small mb-2">
                                                                <i className="fe fe-calendar me-1"></i>
                                                                {interaction.interaction_date ? 
                                                                    new Date(interaction.interaction_date).toLocaleDateString('en-US', {
                                                                        year: 'numeric',
                                                                        month: 'long',
                                                                        day: 'numeric'
                                                                    }) : 'N/A'
                                                                }
                                                                {interaction.interaction_time && (
                                                                    <>
                                                                        <span className="mx-2">•</span>
                                                                        <i className="fe fe-clock me-1"></i>
                                                                        {interaction.interaction_time}
                                                                    </>
                                                                )}
                                                            </div>
                                                            
                                                            {interaction.outcome && (
                                                                <div className="mb-2">
                                                                    <strong>Outcome:</strong> <span className="text-success">{interaction.outcome}</span>
                                                                </div>
                                                            )}
                                                            
                                                            {interaction.notes && (
                                                                <div className="text-muted small">
                                                                    <strong>Notes:</strong> {interaction.notes}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="col-md-4">
                                                    <div className="text-end">
                                                        {interaction.follow_up_date && (
                                                            <div className="alert alert-warning alert-sm mb-2 p-2">
                                                                <small>
                                                                    <i className="fe fe-bell me-1"></i>
                                                                    <strong>Follow-up:</strong><br />
                                                                    {new Date(interaction.follow_up_date).toLocaleDateString('en-US', {
                                                                        month: 'short',
                                                                        day: 'numeric',
                                                                        year: 'numeric'
                                                                    })}
                                                                </small>
                                                            </div>
                                                        )}
                                                        <div className="text-muted small">
                                                            <i className="fe fe-clock me-1"></i>
                                                            Added {interaction.created_at ? 
                                                                new Date(interaction.created_at).toLocaleDateString('en-US', {
                                                                    month: 'short',
                                                                    day: 'numeric'
                                                                }) : 'N/A'
                                                            }
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                    
                </div>
                <Footer />
            </div>
            {/* /Page Wrapper */}

            <DocViewModal doc={viewingDoc} onClose={() => setViewingDoc(null)} />
        </div>
    )
}

export default Leaddetails